import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { Observable, throwError, lastValueFrom } from 'rxjs';
import { ConfigService } from './config.service';

export interface IUser {
  email: string;
  avatarUrl?: string;
  apiToken: string;
}

const defaultPath = '/';

export interface AuthenticateRequest {
  UserName: string;
  Password: string;
}

export interface AuthenticateResponse {
  token: string;
  username: string;
  id: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _user: IUser | null = null;
  private apiUrl: string | undefined;
  private _lastAuthenticatedPath: string = defaultPath;

  constructor(
    private router: Router,
    private http: HttpClient,
    private configService: ConfigService
  ) {
    this.loadUserFromLocalStorage();
    this.initializeApiUrl();
  }

  get loggedIn(): boolean {
    return !!this._user;
  }

  get lastAuthenticatedPath(): string {
    return this._lastAuthenticatedPath;
  }

  set lastAuthenticatedPath(value: string) {
    this._lastAuthenticatedPath = value;
  }

  private loadUserFromLocalStorage() {
    const userToken = localStorage.getItem('userToken');
    const userEmail = localStorage.getItem('userEmail');
    if (userToken && userEmail) {
      this._user = {
        email: userEmail,
        apiToken: userToken
      };
    }
  }

  async initializeApiUrl() {
    if (!this.configService.loaded) {
      await this.configService.loadConfig();
    }
    this.apiUrl = `${this.configService.getConfig().apiBaseUrl}/login`;
  }

  private saveUserToLocalStorage() {
    if (this._user) {
      localStorage.setItem('userToken', this._user.apiToken);
      localStorage.setItem('userEmail', this._user.email);
    }
  }

  private clearUserFromLocalStorage() {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userEmail');
  }

  authenticate(request: AuthenticateRequest): Observable<AuthenticateResponse> {
    if (!this.apiUrl) {
      throw new Error('API URL is not initialized');
    }
    return this.http.post<AuthenticateResponse>(`${this.apiUrl}/Auth`, request).pipe(
      tap(response => console.log('Authentication Response:', response)),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.logOut();
        }
  
        let errorMessage = 'An unknown error occurred!';
        if (error.error instanceof ErrorEvent) {
          errorMessage = `Error: ${error.error.message}`;
        } else {
          if (error.status === 400 && error.error && error.error[0]?.ErrorCode === "INCORRECT_LOGIN") {
            errorMessage = 'Incorrect username and/or password!';
          } else {
            errorMessage = `Server error: ${error.status}\nMessage: ${error.message}`;
          }
        }
        return throwError(errorMessage);
      })
    );
  }
  
  async logIn(email: string, password: string) {
    try {
      if (!this.apiUrl) {
        await this.initializeApiUrl();
      }
  
      const loginRequest: AuthenticateRequest = { UserName: email, Password: password }; 
      this._user = null;
  
      const response = await lastValueFrom(this.authenticate(loginRequest));
      if (!response || !response.token) {
        throw new Error('Authentication failed: no token returned');
      }
  
      this._user = {
        email: email,
        apiToken: response.token
      };
      this.saveUserToLocalStorage();
      this.router.navigate(['/home']);
  
      return {
        isOk: true,
        data: this._user
      };
    } catch (error: unknown) {
      console.error('Authentication error:', error);
  
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.logOut();
      }
  
      return {
        isOk: false,
        message: 'Authentication failed'
      };
    }
  }
  
  async getUser() {
    if (this._user === null) {
      this.router.navigate(['/login-form']);
      return {
        isOk: false,
        data: null
      };
    } else {
      return {
        isOk: true,
        data: this._user
      };
    }
  }

  async changePassword(email: string, recoveryCode: string) {
    try {

      return {
        isOk: true
      };
    } catch {
      return {
        isOk: false,
        message: "Failed to change password"
      };
    }
  }

  async resetPassword(email: string) {
    try {

      return {
        isOk: true
      };
    } catch {
      return {
        isOk: false,
        message: "Failed to reset password"
      };
    }
  }

  async logOut() {
    this._user = null;
    this.clearUserFromLocalStorage();
    this.router.navigate(['/login-form']);
  }

  getToken(): string | null {
    const token = localStorage.getItem('userToken');
    if (!token) {
      this.router.navigate(['/login-form']);
    }
    return token;
  }
}


@Injectable()
export class AuthGuardService implements CanActivate {
  constructor(private router: Router, private authService: AuthService) { }

  canActivate(route: ActivatedRouteSnapshot): boolean {
    const isLoggedIn = this.authService.loggedIn;
    const isAuthForm = [
      'login-form',
      'reset-password',
      'create-account',
      'change-password/:recoveryCode'
    ].includes(route.routeConfig?.path || defaultPath);

    if (isLoggedIn && isAuthForm) {
      this.authService.lastAuthenticatedPath = defaultPath;
      this.router.navigate([defaultPath]);
      return false;
    }

    if (!isLoggedIn && !isAuthForm) {
      this.router.navigate(['/login-form']);
    }

    if (isLoggedIn) {
      this.authService.lastAuthenticatedPath = route.routeConfig?.path || defaultPath;
    }

    return isLoggedIn || isAuthForm;
  }
  
}